/**
 * Modal showing the immutable content snapshot a verdict was reached against (#84).
 *
 * The live page (and the mutable source cache) may have changed since
 * verification; this is the frozen text the verdict actually read, with its
 * content hash as the integrity reference.
 */

import { useEffect, useState } from 'react'
import { resultsApi } from '../services/api'
import type { SnapshotData } from '../types'

interface SourceSnapshotModalProps {
  resultId: string
  factKey: string
  onClose: () => void
}

const PROVENANCE_LABELS: Record<SnapshotData['source_provenance'], string> = {
  fetched: 'Fetched from URL',
  user_uploaded: 'Uploaded by a user',
  supplied_text: 'Text supplied by the caller (not independently fetched)',
  unknown: 'Provenance not recorded',
}

export const SourceSnapshotModal = ({ resultId, factKey, onClose }: SourceSnapshotModalProps) => {
  const [snapshot, setSnapshot] = useState<SnapshotData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    resultsApi
      .getFactSnapshot(resultId, factKey)
      .then((data) => {
        if (!cancelled) setSnapshot(data)
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || 'Failed to load snapshot')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [resultId, factKey])

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '800px',
          width: '90%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Source content used</h2>
        <p style={{ color: '#555', marginTop: 0, fontSize: '0.9rem' }}>
          The exact text this verdict was reached against, frozen at verification time. The live
          page may have changed since.
        </p>

        {loading && <p>Loading snapshot…</p>}
        {error && <p style={{ color: '#c0392b' }}>{error}</p>}

        {snapshot && (
          <>
            <div
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                padding: '0.75rem 1rem',
                fontSize: '0.85rem',
                color: '#555',
                marginBottom: '1rem',
              }}
            >
              {snapshot.url && (
                <div style={{ wordBreak: 'break-all' }}>
                  <strong>URL:</strong>{' '}
                  <a href={snapshot.url} target="_blank" rel="noopener noreferrer">
                    {snapshot.url}
                  </a>
                </div>
              )}
              <div>
                <strong>Origin:</strong> {PROVENANCE_LABELS[snapshot.source_provenance]}
              </div>
              <div>
                <strong>Pinned:</strong>{' '}
                {snapshot.created_at ? new Date(snapshot.created_at).toLocaleString() : 'unknown'}
                {snapshot.content_length != null && (
                  <span> · {snapshot.content_length.toLocaleString()} chars</span>
                )}
              </div>
              <div style={{ wordBreak: 'break-all' }}>
                <strong>Content hash (sha256):</strong>{' '}
                <code style={{ fontSize: '0.8em' }}>{snapshot.content_hash}</code>
              </div>
            </div>

            <div
              style={{
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                padding: '1rem',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                color: '#2c3e50',
                flex: 1,
                minHeight: 0,
              }}
            >
              {snapshot.extracted_text}
            </div>
          </>
        )}

        <div style={{ marginTop: '1rem', textAlign: 'right' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '6px',
              border: '1px solid #ccc',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
